import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'https://dashboard.hekayet-hadaf.com/api'
const TOKEN_KEY = 'diraya3_access_token'

export const api = axios.create({
  baseURL: API_URL,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
})

type RetryableRequest = InternalAxiosRequestConfig & { _authRetry?: boolean }
let refreshRequest: Promise<string> | null = null

const refreshAccessToken = async () => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) throw new Error('No access token to refresh')
  if (!refreshRequest) {
    refreshRequest = axios.post(`${API_URL}/refresh`, {}, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      timeout: 30_000,
    }).then(({ data }) => {
      localStorage.setItem(TOKEN_KEY, data.access_token)
      return data.access_token as string
    }).finally(() => { refreshRequest = null })
  }
  return refreshRequest
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as RetryableRequest | undefined
    const hasToken = Boolean(localStorage.getItem(TOKEN_KEY))
    const canRefresh = error.response?.status === 401 && hasToken && request && !request._authRetry
      && !String(request.url || '').endsWith('/login') && !String(request.url || '').endsWith('/refresh')

    if (canRefresh) {
      request._authRetry = true
      try {
        const token = await refreshAccessToken()
        request.headers.Authorization = `Bearer ${token}`
        return api(request)
      } catch {
        // The refresh window also expired or the token was revoked.
      }
    }
    if (error.response?.status === 401 && hasToken) {
      localStorage.removeItem(TOKEN_KEY)
      window.dispatchEvent(new Event('diraya:unauthorized'))
    }
    return Promise.reject(error)
  },
)

export const auth = {
  token: () => localStorage.getItem(TOKEN_KEY),
  login: async (email: string, password: string) => {
    const { data } = await api.post('/login', { email, password })
    localStorage.setItem(TOKEN_KEY, data.access_token)
    return data
  },
  logout: async () => {
    try { await api.post('/logout') } finally { localStorage.removeItem(TOKEN_KEY) }
  },
}

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const errors = error.response?.data?.errors
    if (errors) return Object.values(errors).flat().join('، ')
    return error.response?.data?.message || 'تعذر إتمام الطلب. حاول مرة أخرى.'
  }
  return 'حدث خطأ غير متوقع.'
}
